/* tslint:disable */
/* eslint-disable */
/**
 * AI Telemetry Hub — Read/Query API
 * Published integration contract for the AI Telemetry Hub read/query surface, generated code-first from AITH.Api and CI-diffed against the committed snapshot. This document is the stable integration contract for its stable operations; each operation\'s maturity and owning story are annotated on the operation itself as its first description line (`Owner: AITH-NN · Maturity: …`, or `Owner: GH-NN` where the owning record is a GitHub issue rather than a Jira story). Operations marked `Published (semantics owned by …)` are published here while their endpoint semantics remain owned by that record. Producers sending telemetry want the separate ingestion contract instead.  ## Authorization  Every operation requires a bearer token (Entra ID or Jonas IDP). The normative rule:  > Authenticated tenant scope is the source of authorization truth. A caller-supplied client code, where accepted, is only a candidate resource selector. It must be canonicalized and authorized against the resolved scope before data access. It must never replace the authenticated scope or broaden access.  Operations fall into four authorization categories:  - **A** — tenant derived from the token only; no client code accepted; all-tenant (staff) scope rejected: `GET /api/v1/client-usage`, `GET /api/v1/dashboard/home`, `GET /api/v1/dashboard/today`, `POST /api/v1/realtime/negotiate`. - **A-staff** — staff-only all-tenant aggregate; no client code accepted; single-tenant scope rejected: `GET /api/v1/dashboard/overview`, `GET /api/v1/dashboard/cost-allocation`, `GET /api/v1/dashboard/cost-by-model`, `GET /api/v1/dashboard/budget-alerts`, `GET /api/v1/dashboard/filter-options` (the applications, models and providers observed across every client, for the staff filter bar; no client appears in it), `GET /api/v1/clients` (the internal client directory — it enumerates registered clients rather than reading telemetry, so there is no tenant to select), `GET /api/v1/unregistered-clients` (the codes observed in telemetry that have no reference record — it **additionally requires the internal administrator role**, unlike every other operation in this category, because the set exists only to feed the configuration write and a non-administrator has no action to take on it). - **B** — the path `clientCode` is a candidate resource selector, canonicalized then authorized against the resolved token scope; a malformed or non-owned code yields the documented denial, never 400: `GET /api/v1/clients/{clientCode}/events`, `GET /api/v1/clients/{clientCode}/events/{eventId}` (cross-tenant and not-found indistinguishable: 404), `GET /api/v1/clients/{clientCode}/summary`, `PUT /api/v1/clients/{clientCode}/configuration` (additionally requires the internal administrator role), `POST /api/v1/clients/{clientCode}/exports`, `GET /api/v1/clients/{clientCode}/exports` (the client\'s own export jobs, newest-first and bounded — never a cross-client listing, because the operation has no form in which to ask for one), `GET /api/v1/clients/{clientCode}/exports/{jobId}` (cross-tenant and not-found indistinguishable: 404). - **C** — dual-mode staff drill-down; the `clientCode` query parameter is structurally optional, but staff callers MUST supply it and client-scoped callers MUST NOT: `GET /api/v1/dashboard/usage`.  ## Writes  This document contains three write operations, of different kinds. `PUT /api/v1/clients/{clientCode}/configuration` is a **resource** write: an administrator-only configuration change with optimistic-concurrency preconditions. `POST /api/v1/realtime/negotiate` is a **credential-minting** write: it writes no resource a caller can read back, but it issues a bounded-lifetime connection grant and records the server-side group membership that grant depends on, so it must never be cached and cannot be a GET. `POST /api/v1/clients/{clientCode}/exports` is this document\'s first **POST-create of a new server-identified resource**: it records an export job the server names, returns that name in `Location`, and answers 200 with the existing job when an identical request is already active, so a repeated submission creates nothing. Every other operation is a read.  ## Error responses  Authentication failures return a uniform 401 bearer challenge with no body guaranteed. Where an operation declares a 500 response it is declared status-only: no response body is guaranteed on a 500 (a deliberate documentation decision matching the runtime).
 *
 * The version of the OpenAPI document: 1.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { AppSpendEntryTotalSpend } from './AppSpendEntryTotalSpend';
import {
    AppSpendEntryTotalSpendFromJSON,
    AppSpendEntryTotalSpendFromJSONTyped,
    AppSpendEntryTotalSpendToJSON,
    AppSpendEntryTotalSpendToJSONTyped,
} from './AppSpendEntryTotalSpend';
import type { PeriodComparison } from './PeriodComparison';
import {
    PeriodComparisonFromJSON,
    PeriodComparisonFromJSONTyped,
    PeriodComparisonToJSON,
    PeriodComparisonToJSONTyped,
} from './PeriodComparison';

/**
 * 
 * @export
 * @interface GroupedUsageRow
 */
export interface GroupedUsageRow {
    /**
     * 
     * @type {string}
     * @memberof GroupedUsageRow
     */
    dimensionValue: string;
    /**
     * 
     * @type {string}
     * @memberof GroupedUsageRow
     */
    dimensionState: string;
    /**
     * 
     * @type {string}
     * @memberof GroupedUsageRow
     */
    metricName: string;
    /**
     * 
     * @type {AppSpendEntryTotalSpend}
     * @memberof GroupedUsageRow
     */
    metricValue: AppSpendEntryTotalSpend;
    /**
     * 
     * @type {string}
     * @memberof GroupedUsageRow
     */
    currency: string | null;
    /**
     * 
     * @type {PeriodComparison}
     * @memberof GroupedUsageRow
     */
    comparison?: PeriodComparison | null;
}

/**
 * Check if a given object implements the GroupedUsageRow interface.
 */
export function instanceOfGroupedUsageRow(value: object): value is GroupedUsageRow {
    if (!('dimensionValue' in value) || value['dimensionValue'] === undefined) return false;
    if (!('dimensionState' in value) || value['dimensionState'] === undefined) return false;
    if (!('metricName' in value) || value['metricName'] === undefined) return false;
    if (!('metricValue' in value) || value['metricValue'] === undefined) return false;
    if (!('currency' in value) || value['currency'] === undefined) return false;
    return true;
}

export function GroupedUsageRowFromJSON(json: any): GroupedUsageRow {
    return GroupedUsageRowFromJSONTyped(json, false);
}

export function GroupedUsageRowFromJSONTyped(json: any, ignoreDiscriminator: boolean): GroupedUsageRow {
    if (json == null) {
        return json;
    }
    return {
        
        'dimensionValue': json['dimensionValue'],
        'dimensionState': json['dimensionState'],
        'metricName': json['metricName'],
        'metricValue': AppSpendEntryTotalSpendFromJSON(json['metricValue']),
        'currency': json['currency'],
        'comparison': json['comparison'] === undefined ? undefined : json['comparison'] === null ? null : PeriodComparisonFromJSON(json['comparison']),
    };
}

export function GroupedUsageRowToJSON(json: any): GroupedUsageRow {
    return GroupedUsageRowToJSONTyped(json, false);
}

export function GroupedUsageRowToJSONTyped(value?: GroupedUsageRow | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'dimensionValue': value['dimensionValue'],
        'dimensionState': value['dimensionState'],
        'metricName': value['metricName'],
        'metricValue': AppSpendEntryTotalSpendToJSON(value['metricValue']),
        'currency': value['currency'],
        'comparison': PeriodComparisonToJSON(value['comparison']),
    };
}

